import React, { useCallback } from 'react'
import classNames from 'classnames'
import { Case, Switch } from 'react-if'
import { GuildScore } from '@/atoms/GuildScore'
import { Image } from '@/atoms/Image'
import { CaptionLG, CaptionMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useLegacyEffect } from '@/molecules/utils/utils'
import { useRecommendationTracking } from '@/services/RecommendedBannerService/useRecommendationTracking'
import { RelatedTitleContentV2 } from '@/types/codegen-federation'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export interface SuggestedContentCardProps {
  recommendedItem?: RelatedTitleContentV2
  className?: string
}

export const SuggestedContentCard: React.FC<SuggestedContentCardProps> = ({ recommendedItem, className }) => {
  const { trackRecommendation } = useRecommendationTracking()
  const { t } = useTranslate('watch')
  const track = useSafeTrack()

  const handleRecommendationClick = useCallback(
    (slug?: string | null) => {
      track('Suggested Shows and Films Item Clicked', { projectSlug: slug ?? '', context: 'Watch Project Page' })
    },
    [track],
  )

  const onClick = useCallback(() => {
    if (!recommendedItem) return
    handleRecommendationClick(recommendedItem?.title?.project?.slug)
    trackRecommendation({ type: 'PRESS', items: [{ id: recommendedItem.id as string }] })
  }, [handleRecommendationClick, trackRecommendation, recommendedItem])

  useLegacyEffect(() => {
    if (!recommendedItem) return
    trackRecommendation({ type: 'IMPRESSION', items: [{ id: recommendedItem.id as string }] })
  }, [trackRecommendation, recommendedItem?.id, recommendedItem])

  if (!recommendedItem || !recommendedItem?.title?.image?.cloudinaryPath) return null

  return (
    <div
      aria-label="suggested-content-card"
      id={recommendedItem?.id ?? ''}
      className="relative min-w-[250px] pr-6 lg:min-w-[316px]"
    >
      <div className={classNames('flex flex-col', className)}>
        {recommendedItem?.title?.image?.cloudinaryPath && (
          <a
            aria-label={recommendedItem.title?.name ?? ''}
            href={`${paths.watch.index}/${recommendedItem?.title?.project?.slug}`}
            className="aspect-h-3 aspect-w-2 relative mb-3 w-full cursor-pointer overflow-hidden rounded-2xl"
            onClick={onClick}
          >
            <div className="absolute inset-0">
              <Image
                alt={recommendedItem?.title?.name ?? ''}
                src={getCloudinaryImageUrl({ path: recommendedItem?.title?.image?.cloudinaryPath })}
                width={316}
                height={474}
                className="h-full w-full object-cover"
              />
            </div>
          </a>
        )}
        <div className="flex flex-col gap-y-1">
          <CaptionMD color="core-gray-500">
            <Switch>
              <Case condition={recommendedItem?.title?.__typename === 'ContentSeries'}>
                <Translate i18nKey="series" t={t}>
                  Series
                </Translate>
              </Case>
              <Case condition={recommendedItem?.title?.__typename === 'ContentMovie'}>
                <Translate i18nKey="movie" t={t}>
                  Movie
                </Translate>
              </Case>
              <Case condition={recommendedItem?.title?.__typename === 'ContentSpecial'}>
                <Translate i18nKey="special" t={t}>
                  Special
                </Translate>
              </Case>
            </Switch>
          </CaptionMD>
          <CaptionLG weight="bold">{recommendedItem?.title?.name}</CaptionLG>
          {recommendedItem?.title?.project?.highestScore ? (
            <GuildScore guildScore={recommendedItem?.title?.project?.highestScore} />
          ) : null}
        </div>
      </div>
    </div>
  )
}
